/* eslint-disable no-nested-ternary */
import { DeviceTypes } from 'types/aspen/device';
import { AspenLeaf } from 'types/aspen/leaf';
import { AppDeviceLeaf, AppGroupLeaf, AppLeaf } from 'types/common/leaf';

export const groupByLeafType = (leaves: Record<AppLeaf['type'], AppLeaf>) =>
  Object.fromEntries(
    Object.entries(leaves).reduce<Map<AppLeaf['type'], Record<AppLeaf['type'], AppLeaf>>>(
      (groupedLeaves, [id, leaf]) =>
        groupedLeaves.set(leaf.type, { ...groupedLeaves.get(leaf.type), [id]: leaf } as Record<
          AppLeaf['type'],
          AppLeaf
        >),
      new Map<AppLeaf['type'], Record<AppLeaf['type'], AppLeaf>>(),
    ),
  ) as {
    device: Record<AppLeaf['id'], AppDeviceLeaf>;
    group: Record<AppLeaf['id'], AppGroupLeaf>;
  };

export const getOrderFromLeaves = (leaves: Array<AspenLeaf>): Array<AppLeaf['id']> =>
  leaves.reduce(
    (deviceLeavesOrder, leaf) =>
      leaf.type !== 'device'
        ? leaf?.memberLeaves?.length
          ? [...deviceLeavesOrder, ...getOrderFromLeaves(leaf?.memberLeaves)]
          : deviceLeavesOrder
        : [...deviceLeavesOrder, leaf.id],
    [] as Array<AppLeaf['id']>,
  );

export const formatLeaves = (leaves: Array<AspenLeaf>): Record<AppLeaf['id'], AppLeaf> =>
  leaves?.reduce(
    (leafLookup, leaf) =>
      leaf.type !== 'device'
        ? leaf?.memberLeaves?.length
          ? { ...leafLookup, [leaf.id]: { ...leaf, memberLeaves: formatLeaves(leaf?.memberLeaves) } }
          : leafLookup
        : { ...leafLookup, [leaf.id]: leaf },
    {},
  );

export const getDeviceLeafById = (leaves: Record<AppLeaf['id'], AppLeaf>, id: string): AppDeviceLeaf | null =>
  id in leaves
    ? (leaves[id] as AppDeviceLeaf)
    : Object.values(leaves).reduce<AppDeviceLeaf | null>(
        (result, leaf) => result || (leaf.type !== 'device' ? getDeviceLeafById(leaf.memberLeaves, id) : null),
        null,
      );

export const getLeafById = (leaves: Record<AppLeaf['id'], AppLeaf>, id: string): AppLeaf | null =>
  id in leaves
    ? leaves[id]
    : Object.values(leaves).reduce<AppLeaf | null>(
        (result, leaf) => result || (leaf.type !== 'device' ? getLeafById(leaf.memberLeaves, id) : null),
        null,
      );

export const getParentLeafById = (leaves: Record<AppLeaf['id'], AppLeaf>, childId: string): AppGroupLeaf | null =>
  Object.values(leaves).reduce<AppGroupLeaf | null>((result, leaf) => {
    if (result) return result;
    if (leaf.type === 'group' && leaf.memberLeaves[childId]) {
      return leaf as AppGroupLeaf;
    }
    if (leaf.type === 'group') {
      return getParentLeafById(leaf.memberLeaves, childId);
    }
    return null;
  }, null);

export const getRootLeafById = <LeafType extends AppLeaf>(
  leaves: Record<AppLeaf['id'], LeafType>,
  id: AppLeaf['id'],
): LeafType | null =>
  Object.values(leaves).find((leaf) =>
    leaf.type !== 'device' ? getRootLeafById(leaf.memberLeaves, id) : leaf.id === id,
  ) || null;

export const filterLeavesByDeviceType = (
  leaves: Record<AppLeaf['id'], AppLeaf>,
  filterValues: Array<DeviceTypes>,
): Record<AppLeaf['id'], AppLeaf> =>
  Object.fromEntries(
    Object.entries(leaves)
      .filter(([_id, leaf]) =>
        leaf.type !== 'device'
          ? Object.keys(filterLeavesByDeviceType(leaf.memberLeaves, filterValues)).length
          : filterValues.includes(leaf.subType),
      )
      .filter((filteredLeaves) => !!filteredLeaves.length),
  );
